import TextField from '@components/v2/form/TextField/TextField';
import { CircularProgress } from '@mui/material';
import { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import Icon from '../Icon/Icon';
import { useExpandableSearchStyles } from './expandableSearch.styles';
import type { ExpandableSearchHandle, ExpandableSearchProps } from './expandableSearch.types';
import useExpandableSearch from './hooks/useExpandableSearch';

const ExpandableSearch = forwardRef<ExpandableSearchHandle, ExpandableSearchProps>(
  (
    {
      trigger,
      value,
      onChange,
      onEnter,
      placeholder,
      startIcon,
      width = 264,
      action,
      isLoading,
      errorSlot,
      onOpen,
      onClose,
      testId,
      inputProps,
      instantCollapse,
      openClassName,
      size = 'small',
    },
    ref,
  ) => {
    const { classes, cx } = useExpandableSearchStyles({ openWidth: width });
    const { translate } = useTranslation();
    const { isOpen, isTriggerHidden, open, collapse, focus, inputRef, handleBlur, onInputKeyDown } =
      useExpandableSearch({ onOpen, onClose, onEnter, value });

    useImperativeHandle(ref, () => ({ open, collapse, focus }), [open, collapse, focus]);

    const renderEndAdornment = () => {
      if (isLoading) {
        return <CircularProgress size={16} />;
      }
      if (errorSlot) {
        return errorSlot;
      }
      if (!action) {
        return null;
      }
      return (
        <Icon
          icon={action.icon}
          className={classes.actionIcon}
          onClick={action.onClick}
          onMouseDown={(e) => e.preventDefault()}
        />
      );
    };

    return (
      <div className={cx(classes.root, isOpen && openClassName)} role="search">
        <div className={cx(isTriggerHidden && classes.triggerAbsoluteHidden)}>{trigger(open)}</div>
        <div
          className={cx(
            classes.inputWrapper,
            isOpen ? classes.inputWrapperOpen : classes.inputWrapperClosed,
          )}
          style={instantCollapse && !isOpen ? { transition: 'none' } : undefined}
        >
          <TextField
            className={cx(classes.input, inputProps?.className)}
            ref={inputRef}
            value={value}
            placeholder={translate(placeholder)}
            size={size}
            fullWidth
            onChange={(event) => onChange(event.target.value)}
            onBlur={handleBlur}
            onFocus={(event) => event.target.select()}
            slotProps={{
              input: {
                sx: inputProps?.sx,
                startAdornment: <Icon icon={startIcon} />,
                endAdornment: renderEndAdornment(),
              },
              htmlInput: {
                onKeyDown: onInputKeyDown,
                'test-id': testId,
              },
            }}
          />
        </div>
      </div>
    );
  },
);

ExpandableSearch.displayName = 'ExpandableSearch';
export default ExpandableSearch;
